"use client";

import Link from "next/link";
import { Download } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MotionView, MotionFadeUp } from "@/components/ui/motion-wrapper";
import { IconRenderer } from "@/components/ui/icon-renderer";
import { resolvePublicAssetUrl } from "@/lib/api";

const defaultResources = [
  {
    iconKey: "FileText",
    title: "Cloud Migration Checklist",
    description: "A step-by-step guide to plan and execute a seamless cloud migration for your business.",
    format: "PDF Guide",
    gradient: "from-sky-500 to-blue-600",
  },
  {
    iconKey: "CheckSquare",
    title: "Software Development RFP Template",
    description: "Professionally structured template to help you define requirements and evaluate vendors.",
    format: "DOCX Template",
    gradient: "from-violet-500 to-purple-600",
  },
  {
    iconKey: "BookOpen",
    title: "AI Implementation Playbook",
    description: "Learn how to identify AI opportunities, build a roadmap, and measure ROI effectively.",
    format: "PDF Playbook",
    gradient: "from-emerald-500 to-teal-600",
  },
];

export function LeadMagnets({
  eyebrow = "Free Resources",
  heading = "Guides & Templates to Get You Started",
  description = "Download our free resources — built from real project experience.",
  resources = defaultResources,
}: {
  eyebrow?: string;
  heading?: string;
  description?: string;
  resources?: Array<{
    iconKey?: string;
    title: string;
    description: string;
    format: string;
    gradient: string;
    href?: string;
    category?: string;
    fileSize?: number | null;
  }>;
}) {
  return (
    <section className="py-14 lg:py-28 gradient-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <MotionView className="text-center max-w-3xl mx-auto mb-16">
          <MotionFadeUp>
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">
              {eyebrow}
            </span>
          </MotionFadeUp>
          <MotionFadeUp>
            <h2 className="mt-3 text-3xl lg:text-5xl font-bold text-foreground">
              {heading}
            </h2>
          </MotionFadeUp>
          <MotionFadeUp>
            <p className="mt-4 text-lg text-muted-foreground">
              {description}
            </p>
          </MotionFadeUp>
        </MotionView>

        <MotionView className="grid md:grid-cols-3 gap-6">
          {resources.map((resource) => (
            <MotionFadeUp key={resource.title}>
              <Card className="h-full bg-card/80 backdrop-blur-sm border-border hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
                <CardContent className="p-6 flex flex-col h-full">
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${resource.gradient} flex items-center justify-center mb-4 shadow-md`}
                  >
                    <IconRenderer iconKey={resource.iconKey} fallback={Download} className="w-6 h-6 text-white" emojiClassName="text-2xl leading-none" />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                    {resource.format}
                  </span>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{resource.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6 flex-1">
                    {resource.description}
                  </p>
                  <div className="mb-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {resource.category ? <span className="rounded-full bg-muted px-2.5 py-1">{resource.category}</span> : null}
                    {resource.fileSize ? <span className="rounded-full bg-muted px-2.5 py-1">{Math.max(resource.fileSize / 1024, 0.1).toFixed(1)} MB</span> : null}
                  </div>
                  <Button asChild variant="outline" size="sm" className="w-full group-hover:border-primary group-hover:text-primary transition-colors">
                    <Link href={resolvePublicAssetUrl(resource.href)} target="_blank" rel="noreferrer" download>
                      <Download className="w-4 h-4 mr-2" />
                      Download Free
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </MotionFadeUp>
          ))}
        </MotionView>
      </div>
    </section>
  );
}
